import React from 'react';
import { motion } from 'framer-motion';
import { HeartIcon } from 'lucide-react';
import type { SlideProps } from './SlideShell';
interface FloatingHeart {
  left: number;
  size: number;
  delay: number;
  duration: number;
  drift: number;
  opacity: number;
  filled: boolean;
}
const HEARTS: FloatingHeart[] = [{
  left: 6,
  size: 46,
  delay: 0,
  duration: 13,
  drift: 26,
  opacity: 0.9,
  filled: true
}, {
  left: 14,
  size: 26,
  delay: 3.4,
  duration: 15,
  drift: -18,
  opacity: 0.55,
  filled: false
}, {
  left: 22,
  size: 34,
  delay: 1.6,
  duration: 11.5,
  drift: 20,
  opacity: 0.75,
  filled: true
}, {
  left: 31,
  size: 22,
  delay: 5.2,
  duration: 14,
  drift: -24,
  opacity: 0.5,
  filled: false
}, {
  left: 39,
  size: 40,
  delay: 2.4,
  duration: 16,
  drift: 14,
  opacity: 0.35,
  filled: true
}, {
  left: 48,
  size: 24,
  delay: 6.4,
  duration: 12.5,
  drift: -12,
  opacity: 0.6,
  filled: false
}, {
  left: 57,
  size: 32,
  delay: 0.8,
  duration: 15.5,
  drift: 22,
  opacity: 0.45,
  filled: true
}, {
  left: 66,
  size: 20,
  delay: 4.2,
  duration: 12,
  drift: -20,
  opacity: 0.65,
  filled: false
}, {
  left: 74,
  size: 44,
  delay: 2.8,
  duration: 14.5,
  drift: 18,
  opacity: 0.85,
  filled: true
}, {
  left: 83,
  size: 28,
  delay: 6.8,
  duration: 13,
  drift: -16,
  opacity: 0.5,
  filled: false
}, {
  left: 91,
  size: 36,
  delay: 1.2,
  duration: 16.5,
  drift: 24,
  opacity: 0.7,
  filled: true
}, {
  left: 97,
  size: 22,
  delay: 4.8,
  duration: 12.8,
  drift: -14,
  opacity: 0.45,
  filled: false
}];
export function SlideLoveCover({}: SlideProps) {
  return <div className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden bg-[var(--surface)] px-[clamp(2rem,7vw,7rem)] text-center" data-id="element-299" data-mp="tlx0lcp2dfal">
      <motion.div initial={{
      scaleX: 0
    }} animate={{
      scaleX: 1
    }} transition={{
      duration: 0.8,
      ease: 'easeOut'
    }} className="absolute left-0 top-0 h-2 w-full origin-left bg-[var(--brand)]" data-id="element-300" data-mp="18ce20w1p857h" />

      {/* Balloon hearts rising */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true" data-id="element-301" data-mp="au46fey9do1a">
        {HEARTS.map(heart => <motion.div key={`${heart.left}-${heart.delay}`} className="absolute bottom-[-8%]" style={{
        left: `${heart.left}%`
      }} initial={{
        y: 0,
        x: 0,
        opacity: 0
      }} animate={{
        y: ['0%', '-125vh'],
        x: [0, heart.drift, -heart.drift * 0.6, 0],
        opacity: [0, heart.opacity, heart.opacity, 0]
      }} transition={{
        duration: heart.duration,
        delay: heart.delay,
        repeat: Infinity,
        ease: 'linear',
        times: [0, 0.12, 0.82, 1]
      }} data-id="element-302" data-mp="10ijarwpph5j6">
            <motion.div animate={{
          scale: [1, 1.14, 1],
          rotate: [-6, 6, -6]
        }} transition={{
          duration: 2.6,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: heart.delay * 0.4
        }} data-id="element-303" data-mp="vjydo65r31yy">
              <HeartIcon className={heart.filled ? 'text-[var(--brand)] fill-current' : 'text-[var(--brand)]'} style={{
            width: heart.size,
            height: heart.size
          }} strokeWidth={heart.filled ? 0 : 2.4} data-id="element-304" data-mp="3rh5xk5wsl5nu" />
            </motion.div>
          </motion.div>)}
      </div>

      <div className="relative flex max-w-6xl flex-col items-center" data-id="element-305" data-mp="38qo4u1byma2h">
        <motion.span initial={{
        scaleX: 0
      }} animate={{
        scaleX: 1
      }} transition={{
        delay: 0.2,
        duration: 0.5
      }} className="mb-[clamp(1.25rem,2.5vw,2.25rem)] h-1.5 w-16 rounded-full bg-[var(--brand)]" data-id="element-306" data-mp="38ol7sxce3go9" />

        <motion.h1 initial={{
        opacity: 0,
        y: 26
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3,
        duration: 0.75,
        ease: 'easeOut'
      }} className="text-[clamp(3rem,9vw,9rem)] font-extrabold leading-[0.92] tracking-[-0.055em] text-[var(--fg)]" data-id="element-307" data-mp="2tu6y5neptjm9">
          All you need is love.
        </motion.h1>

        <motion.p initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.65,
        duration: 0.55
      }} className="mt-[clamp(1.25rem,2.4vw,2rem)] text-[clamp(0.95rem,1.5vw,1.5rem)] font-semibold tracking-[-0.01em] text-[var(--muted)]" data-id="element-308" data-mp="100nbz9m0ewsw">
          Attention makes things thrive.
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.85,
        duration: 0.55
      }} className="mt-[clamp(1rem,2vw,1.75rem)] flex flex-wrap items-center justify-center gap-x-[clamp(0.75rem,1.6vw,1.5rem)] gap-y-2" data-id="element-309" data-mp="hbgsvlhb036k">
          {['Short.', 'Sharp.', 'Satisfying.'].map((word, index) => <span key={word} className="flex items-center gap-[clamp(0.75rem,1.6vw,1.5rem)]" data-id="element-310" data-mp="3uiijkyksh41u" data-mpi={index}>
              {index > 0 && <span className="h-1.5 w-1.5 rounded-full bg-[var(--brand)]/40" aria-hidden="true" data-id="element-311" data-mp="3fw202xhyicav" />}
              <span className="text-[clamp(1rem,1.9vw,1.85rem)] font-extrabold tracking-[-0.03em] text-[var(--brand)]" data-id="element-312" data-mp="3ayteso1ihs5e">
                {word}
              </span>
            </span>)}
        </motion.div>

        <motion.p initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1.05,
        duration: 0.6
      }} className="mt-[clamp(1rem,2.2vw,1.9rem)] text-[clamp(1.4rem,3vw,3rem)] font-extrabold leading-tight tracking-[-0.03em] text-[var(--fg)]" data-id="element-313" data-mp="3jzri1444lvmx">
          Do your brands get the attention they deserve?
        </motion.p>
      </div>
    </div>;
}